import { ApiError, apiRequest } from '../../../../api/client';
import type { InvitationDelivery } from '../../../../users/api';

/**
 * Mirrors `apps/api` `customer-portal-access.dto.ts`'s `PORTAL_ROLE_CODES`
 * tuple: the three client-facing roles a portal user can hold.
 */
export const PORTAL_ROLE_CODES = ['CLIENT_VIEWER', 'CLIENT_BUYER', 'CLIENT_MANAGER'] as const;
export type PortalRoleCode = (typeof PORTAL_ROLE_CODES)[number];

/** Quoted verbatim from `apps/api` `role-catalog.ts` `description` per role. */
export const PORTAL_ROLE_LABELS: Record<PortalRoleCode, string> = {
  CLIENT_VIEWER: 'Consulta de Cliente',
  CLIENT_BUYER: 'Encargado de Compras',
  CLIENT_MANAGER: 'Supervisor de Cliente',
};

export const PORTAL_ROLE_DESCRIPTIONS: Record<PortalRoleCode, string> = {
  CLIENT_VIEWER: 'Solo lectura de pedidos, remitos y facturas de su empresa.',
  CLIENT_BUYER: 'Carga y gestiona pedidos de su empresa y sedes.',
  CLIENT_MANAGER: 'Todo lo del encargado, más gestiona encargados y ve facturas de su empresa.',
};

/**
 * `role-catalog.ts`: only `CLIENT_BUYER` and `CLIENT_MANAGER` hold
 * `order.create`. `PortalAccessInviteModal` derives `canOrder` from the
 * chosen role instead of exposing it as its own field
 * (`tasks.md` PR 5: "canOrder implícito del rol").
 */
export function canOrderForRole(roleCode: PortalRoleCode): boolean {
  return roleCode !== 'CLIENT_VIEWER';
}

/** Matches `apps/api` `PortalAccessSummary` (`customer-portal-access.repository.ts`). */
export interface PortalAccessSummary {
  id: string;
  userId: string;
  email: string;
  fullName: string;
  roleCode: PortalRoleCode | null;
  /** Empty means every site of the customer. */
  siteIds: string[];
  canOrder: boolean;
  dailyQuantityLimit: number | null;
  validFrom: string;
  validTo: string | null;
  invitationStatus: 'PENDING' | 'ACCEPTED';
}

/** Matches `apps/api` `createPortalAccessSchema`. */
export interface InvitePortalAccessInput {
  contactId: string;
  roleCode: PortalRoleCode;
  siteIds: string[];
  canOrder: boolean;
  dailyQuantityLimit?: number | null;
  /** Calendar day, `YYYY-MM-DD`. Open-ended when absent. */
  validFrom?: string;
  /** Calendar day, `YYYY-MM-DD`. No expiry when absent or `null`. */
  validTo?: string | null;
}

export interface InvitePortalAccessResult {
  userId: string;
  invitationId: string;
  expiresAt: string;
  delivery: InvitationDelivery;
}

/** Matches `apps/api` `updatePortalAccessSchema`: no `roleCode`, rejected by the server's `.strict()` if sent. */
export interface UpdatePortalAccessInput {
  siteIds?: string[];
  canOrder?: boolean;
  dailyQuantityLimit?: number | null;
  validFrom?: string;
  validTo?: string | null;
}

export function listPortalAccess(customerId: string): Promise<PortalAccessSummary[]> {
  return apiRequest<PortalAccessSummary[]>(`/customers/${customerId}/portal-access`);
}

export function invitePortalAccess(
  customerId: string,
  input: InvitePortalAccessInput,
): Promise<InvitePortalAccessResult> {
  return apiRequest<InvitePortalAccessResult>(`/customers/${customerId}/portal-access`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function updatePortalAccess(
  customerId: string,
  accessId: string,
  input: UpdatePortalAccessInput,
): Promise<PortalAccessSummary> {
  return apiRequest<PortalAccessSummary>(`/customers/${customerId}/portal-access/${accessId}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

export function revokePortalAccess(customerId: string, accessId: string): Promise<void> {
  return apiRequest<void>(`/customers/${customerId}/portal-access/${accessId}`, {
    method: 'DELETE',
  });
}

/**
 * `code` field driven, never `message` parsing, same convention as
 * `mapContactActionError`/`mapSiteError`. Shared by invite/edit/revoke —
 * the REFACTOR task of `tasks.md` PR 5 asks for exactly one mapping, not
 * one per action.
 */
export function mapPortalAccessError(error: unknown): string {
  if (error === undefined || error === null) {
    return '';
  }

  if (!(error instanceof ApiError)) {
    return 'Ocurrió un error de conexión. Intentá de nuevo.';
  }

  const code = (error.body as { code?: string } | null)?.code;

  if (error.status === 409 && code === 'CONTACT_ALREADY_LINKED') {
    return 'Ese contacto ya tiene un usuario del portal vinculado.';
  }

  if (error.status === 400 && code === 'CONTACT_MISSING_EMAIL') {
    return 'Ese contacto no tiene un email cargado. Agregalo antes de invitarlo.';
  }

  if (error.status === 409 && code === 'EMAIL_ALREADY_REGISTERED') {
    return 'Ya existe un usuario registrado con ese email.';
  }

  if (error.status === 403) {
    return 'No tenés permiso para realizar esta acción.';
  }

  if (error.status === 404) {
    return 'El acceso ya no existe.';
  }

  if (error.status === 400 || error.status === 422) {
    return 'Revisá los datos ingresados e intentá de nuevo.';
  }

  return 'Ocurrió un error inesperado. Intentá de nuevo.';
}
